import { useState, useRef, useEffect, useCallback, createContext, useContext, Children, isValidElement } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { SeletorNoCelular } from './SeletorNoCelular';

/**
 * Tabs — navegação por abas horizontais.
 *
 * Composição:
 *   <Tabs defaultValue="comercial">
 *     <TabsList>
 *       <TabsTrigger value="comercial">Comercial</TabsTrigger>
 *       <TabsTrigger value="manutencao">Manutenção</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="comercial">...</TabsContent>
 *     <TabsContent value="manutencao">...</TabsContent>
 *   </Tabs>
 *
 * Estilo: underline vermelho no ativo (mesmo padrão do topo nav do app).
 *
 * Pode ser controlado (passando `value` + `onValueChange`) ou descontrolado (passando só `defaultValue`).
 */

interface TabsContextValue {
  value: string;
  setValue: (v: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultValue = '', value: controlledValue, onValueChange, children, className }: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = controlledValue ?? internalValue;

  const setValue = (v: string) => {
    if (controlledValue === undefined) setInternalValue(v);
    onValueChange?.(v);
  };

  return (
    <TabsContext.Provider value={{ value, setValue }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({
  children,
  className,
  seletorNoCelular,
}: {
  children: ReactNode;
  className?: string;
  /**
   * MOBILE-COMPLETO (M2) — porta OPCIONAL: com o nome da área ("Financeiro"),
   * abaixo de `md` a fileira vira uma linha "Financeiro: Visão Geral ▾" que abre
   * a lista de abas no lugar. ⚠️ Sem esta prop nada muda (as outras telas que
   * usam o componente não pediram); com ela, o computador também não muda — o
   * seletor é `md:hidden` e a fileira só some abaixo de `md`.
   */
  seletorNoCelular?: string;
}) {
  const ctx = useContext(TabsContext);
  // FASE-B (design system): rolagem horizontal no mobile — abas NUNCA quebram
  // em 2+ linhas (a ficha do cliente quebrava em 3 e estourava +210px).
  // Setas ← → navegam entre as abas (padrão role=tablist).

  /**
   * 🔴 A PISTA DE QUE HÁ MAIS ABAS (BUGS #161, auditoria de setembro).
   *
   * Medido a 1280px — a largura de referência do projeto: o trilho do Financeiro
   * mostrava **928px de 1141px**, e "Combustível" e "Inconformidades" ficavam
   * fora. A faixa **cortava no meio da palavra**, e a barra de rolagem está
   * escondida de propósito (`[scrollbar-width:none]`, logo abaixo) — ou seja,
   * não havia **nenhuma** indicação de que existiam mais duas abas.
   *
   * ⚠️ Quebrar em duas linhas foi tentado e RECUSADO na FASE-B: a ficha do
   * cliente ia a 3 linhas e estourava +210px, contra a regra de altura
   * (1366×600 no pior caso). Por isso a saída aqui é a rolagem **com pista**:
   * esmaecimento no lado que tem conteúdo escondido, mais uma seta para rolar.
   *
   * Vale para as 20 telas que usam este componente, não só o Financeiro.
   */
  const trilho = useRef<HTMLDivElement>(null);
  const [temEsquerda, setTemEsquerda] = useState(false);
  const [temDireita, setTemDireita] = useState(false);

  const medir = useCallback(() => {
    const el = trilho.current;
    if (!el) return;
    // 1px de folga: arredondamento de layout não pode acender a pista sozinho.
    setTemEsquerda(el.scrollLeft > 1);
    setTemDireita(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = trilho.current;
    if (!el) return;
    medir();
    // O trilho muda de tamanho sem a janela mudar: aba nova, fonte diferente,
    // menu lateral recolhendo. `resize` da janela sozinho não pegaria isso.
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    for (const filho of Array.from(el.children)) obs.observe(filho);
    return () => obs.disconnect();
  }, [medir, children]);

  function rolar(direcao: -1 | 1) {
    const el = trilho.current;
    if (!el) return;
    // ⚠️ `prefers-reduced-motion` respeitado: quem pediu menos movimento salta.
    const suave = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: direcao * Math.max(160, el.clientWidth * 0.6), behavior: suave ? 'smooth' : 'auto' });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const tabs = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
    );
    const atual = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (atual === -1) return;
    e.preventDefault();
    const proximo =
      e.key === 'ArrowRight' ? (atual + 1) % tabs.length : (atual - 1 + tabs.length) % tabs.length;
    tabs[proximo]?.focus();
    tabs[proximo]?.click();
  }

  /**
   * A pista, de um lado. O esmaecimento diz "tem mais para lá"; a seta é o
   * jeito de ir, para quem usa mouse e não sabe rolar na horizontal.
   *
   * ⚠️ `aria-hidden` + `tabIndex={-1}` de propósito: as setas ←/→ do teclado já
   * percorrem as abas (o `handleKeyDown` acima), e quem usa leitor de tela
   * alcança todas pela própria lista. Pôr as setas na ordem de foco só
   * acrescentaria duas paradas que não levam a lugar nenhum novo.
   */
  const Pista = ({ lado }: { lado: 'esquerda' | 'direita' }) => (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute top-0 bottom-px z-10 flex w-16 items-center',
        lado === 'esquerda'
          ? 'left-0 justify-start bg-gradient-to-r'
          : 'right-0 justify-end bg-gradient-to-l',
        'from-paper via-paper/85 to-transparent dark:from-paper-dark dark:via-paper-dark/85',
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        onClick={() => rolar(lado === 'esquerda' ? -1 : 1)}
        className={cn(
          // ⚠️ 32px no celular, contra o piso de 44 — e é EXCEÇÃO DECLARADA.
          // A seta é uma PISTA de que há mais abas (BUGS #161); o gesto
          // principal é arrastar a tira, que tem a altura inteira. Crescer a
          // seta até 44px a faria cobrir a aba vizinha, que é o alvo de
          // verdade. 24 → 32px reduz o erro sem criar outro.
          'pointer-events-auto flex h-8 w-8 sm:h-6 sm:w-6 items-center justify-center rounded-full',
          'border border-line bg-paper-surface text-ink-2 shadow-sm transition-colors',
          'hover:text-ink-1 dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-2 dark:hover:text-ink-dark-1',
        )}
      >
        {lado === 'esquerda' ? '‹' : '›'}
      </button>
    </div>
  );

  const opcoesDoCelular =
    seletorNoCelular && ctx
      ? Children.toArray(children)
          .filter(isValidElement)
          .map((el) => {
            const props = el.props as { value?: string; children?: ReactNode };
            const valor = String(props.value ?? '');
            return {
              chave: valor,
              label: textoDe(props.children),
              ativa: ctx.value === valor,
              onSelecionar: () => ctx.setValue(valor),
            };
          })
      : [];

  return (
    <div className="relative">
      {seletorNoCelular && <SeletorNoCelular className="mb-3" rotulo={seletorNoCelular} opcoes={opcoesDoCelular} />}
      <div
        ref={trilho}
        role="tablist"
        onKeyDown={handleKeyDown}
        onScroll={medir}
        className={cn(
          'flex items-center gap-6 border-b border-line dark:border-line-dark',
          'overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
          seletorNoCelular && 'max-md:hidden',
        )}
      >
        {children}
      </div>
      {temEsquerda && <Pista lado="esquerda" />}
      {temDireita && <Pista lado="direita" />}
    </div>
  );
}

/** O texto de um rótulo de aba (que pode ter número, ícone ou contador dentro). */
function textoDe(no: ReactNode): string {
  if (no == null || typeof no === 'boolean') return '';
  if (typeof no === 'string' || typeof no === 'number') return String(no);
  if (Array.isArray(no)) return no.map(textoDe).join('').trim();
  if (isValidElement(no)) return textoDe((no.props as { children?: ReactNode }).children);
  return '';
}

export function TabsTrigger({
  value,
  children,
  className,
  'data-testid': testId,
}: {
  value: string;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('TabsTrigger deve estar dentro de <Tabs>');

  const isActive = ctx.value === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      type="button"
      data-testid={testId}
      onClick={() => ctx.setValue(value)}
      className={cn(
        'relative pb-3 -mb-px text-sm transition-colors flex-none',
        // ── 🔴 O ALVO DE 44px QUE NUNCA EXISTIU (Frente 2, 12/09/2026).
        //
        // ⚠️ Aqui havia um pseudo-elemento `before:h-11` e um comentário
        // afirmando "44px de alvo, ZERO pixel de mudança visual". **A intenção
        // estava certa e o alvo nunca passou de 38px.**
        //
        // A causa, medida no beta em 375px: a TIRA das abas tem
        // `overflow-x: auto`, e o CSS não permite um eixo `visible` com o
        // outro `auto` — o `overflow-y` vira `auto` junto. Medido na tela:
        // tira com 38px de altura, botão com 38px, **zero sobra acima e
        // abaixo**. Os 6px que o pseudo-elemento pedia eram recortados.
        //
        // 🔴 É a terceira vez neste projeto que um comentário defende um
        // defeito (o `avulsosSql` do BUGS #227 e o alerta do #229 foram as
        // outras). **Comentário que promete o que o código não faz impede a
        // próxima pessoa de desconfiar** — por isso o pseudo-elemento saiu em
        // vez de ficar como enfeite.
        //
        // A cura é altura de verdade, e só no celular: `pt-2.5` leva o botão
        // a 44px. ⚠️ Isso MUDA 6px acima do rótulo em toda tela com abas, no
        // telefone — foi decisão consciente, e o desktop (mouse) não muda.
        // O sublinhado do ativo continua ancorado na base do botão, encostado
        // na borda da tira, exatamente como antes.
        'pt-2.5 sm:pt-1',
        isActive
          ? 'text-red-deep dark:text-red-glow font-semibold'
          : 'text-ink-2 dark:text-ink-dark-2 font-medium hover:text-ink-1 dark:hover:text-ink-dark-1',
        className
      )}
    >
      {children}
      {isActive && (
        <span
          className="absolute left-0 right-0 -bottom-px h-0.5 bg-red"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export function TabsContent({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('TabsContent deve estar dentro de <Tabs>');

  if (ctx.value !== value) return null;

  return <div className={cn('pt-4', className)}>{children}</div>;
}
